import { useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "~/core/axios";
import { REACT_QUERY_KEY } from "~/core/constants/common";
import { BackendApiResponse } from "~/core/types/backend-api-response.type";
import { ObjectType } from "~/core/types/common.type";

import {
  SearchObjectsResponse,
  SearchObjectsWithDataResponse,
} from "~/components/GlobalCommandbar/types/common";

type UseCommonGlobalObjectSearchParams = {
  searchQuery?: string;
};

type UseGlobalObjectSearchParams = UseCommonGlobalObjectSearchParams & {
  objectType?: ObjectType;
  allowEmptySearch?: boolean;
};

type UseGlobalObjectSearchByObjectTypeWithDataParams<T> =
  UseCommonGlobalObjectSearchParams & {
    objectType: ObjectType;
  };

export const useGlobalObjectSearch = ({
  objectType,
  searchQuery,
  allowEmptySearch = false,
}: UseGlobalObjectSearchParams) => {
  const { data: objectSearchData, isLoading: isLoadingObjectSearch } = useQuery(
    {
      queryKey: [REACT_QUERY_KEY.GLOBAL_SEARCH, objectType, searchQuery],
      queryFn: async ({ queryKey }) => {
        const [, objectType, searchQuery = ""] = queryKey;

        const searchQueryString = String(searchQuery)?.trim();

        if (!allowEmptySearch && !searchQueryString) {
          return [];
        }

        const searchQueryParams = new URLSearchParams();
        if (objectType)
          searchQueryParams.set("object_type", String(objectType));
        searchQueryParams.set("q", searchQueryString);

        try {
          const { data } = await axios.get<
            BackendApiResponse<SearchObjectsResponse[]>
          >(`/search-objects?${searchQueryParams.toString()}`);

          return data.data;
        } catch (error) {
          console.error(error);
          return null;
        }
      },
      retry: 1,
      // enabled: allowEmptySearch ? true : !!searchQuery?.trim(),
    },
  );

  return { objectSearchData, isLoadingObjectSearch };
};

export const useGlobalObjectSearchByObjectTypeWithData = <T>({
  objectType,
  searchQuery,
}: UseGlobalObjectSearchByObjectTypeWithDataParams<T>) => {
  const { data: objectSearchData, isLoading: isLoadingObjectSearch } = useQuery(
    {
      queryKey: [
        REACT_QUERY_KEY.GLOBAL_SEARCH_BY_OBJECT_TYPE,
        objectType,
        searchQuery,
      ],
      queryFn: async ({ queryKey }) => {
        const [, objectType, searchQuery = ""] = queryKey;

        const searchQueryParams = new URLSearchParams();
        searchQueryParams.set("q", searchQuery);

        try {
          const { data } = await axios.get<
            BackendApiResponse<SearchObjectsWithDataResponse<T>[]>
          >(`/search-objects/${objectType}?${searchQueryParams.toString()}`);

          return data.data;
        } catch (error) {
          console.error(error);
          return null;
        }
      },
      retry: 1,
      refetchOnMount: "always",
      refetchOnWindowFocus: "always",
    },
  );

  return { objectSearchData, isLoadingObjectSearch };
};

// Invalidations
export const useInvalidateGlobalObjectSearch = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEY.GLOBAL_SEARCH],
    });
};
